import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './TechnicianPortal.css';
import ClienteRegistro from '../components/ClienteRegistro';

const TechnicianPortal = () => {
  const [accessCode, setAccessCode] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [technician, setTechnician] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [filters, setFilters] = useState({
    status: ''
  });
  const [timeRemaining, setTimeRemaining] = useState({});
  const [completionComment, setCompletionComment] = useState('');
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [reportToComplete, setReportToComplete] = useState(null);
  const [showClienteRegistro, setShowClienteRegistro] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [showClientes, setShowClientes] = useState(false);
  
  useEffect(() => {
    // Verificar si hay un código guardado
    const savedCode = localStorage.getItem('technicianAccessCode');
    if (savedCode) {
      setAccessCode(savedCode);
      handleLogin(savedCode);
    }
  }, []);

  const loadReports = useCallback(async () => {
    if (!accessCode) {
      console.warn('⚠️ No hay código de acceso disponible para cargar reportes');
      return;
    }

    try {
      setLoading(true);
      const params = {
        ...filters
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      console.log('📡 Cargando reportes para técnico con código:', accessCode);
      const response = await axios.get(`/api/technicians/reports/${accessCode}`, {
        params
      });

      console.log('✅ Respuesta de reportes:', response.data);
      
      if (response.data.success) {
        setReports(response.data.data || []);
        // Cargar información actualizada del técnico (con puntos) solo si hay cambios
        if (response.data.technician) {
          setTechnician(prev => {
            // Solo actualizar si hay cambios en los puntos
            if (prev &&
                prev.puntos === response.data.technician.puntos &&
                prev.puntosPositivos === response.data.technician.puntosPositivos &&
                prev.puntosNegativos === response.data.technician.puntosNegativos) {
              return prev; // No actualizar si no hay cambios
            }
            return {
              ...prev,
              ...response.data.technician
            };
          });
        }
      } else {
        console.error('❌ Error en respuesta:', response.data);
        alert('Error al cargar reportes: ' + (response.data.error || 'Error desconocido'));
      }
    } catch (error) {
      console.error('❌ Error cargando reportes:', error);
      console.error('   Detalles:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.message || 'Error desconocido';
      alert('Error al cargar reportes: ' + errorMessage);
      setReports([]); // Asegurar que se limpia el estado en caso de error
    } finally {
      setLoading(false);
    }
  }, [accessCode, filters]);

  const loadClientes = useCallback(async () => {
    if (!accessCode) return;
    
    try {
      const response = await axios.get('/api/clientes', {
        headers: { 'x-access-code': accessCode }
      });
      
      if (response.data.success) {
        setClientes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error cargando clientes:', error);
    }
  }, [accessCode]);

  useEffect(() => {
    if (authenticated && accessCode) {
      loadReports();
      loadClientes();
    }
  }, [authenticated, accessCode, filters, loadReports, loadClientes]);

  // Actualizar contadores de tiempo en tiempo real
  useEffect(() => {
    if (!authenticated || reports.length === 0) return;

    const interval = setInterval(() => {
      const newTimeRemaining = {};
      reports.forEach(report => {
        if (report.tiempoInicio && report.status !== 'completado' && report.tiempoLimite) {
          const startTime = new Date(report.tiempoInicio).getTime();
          const now = new Date().getTime();

          // Si la fecha de inicio es en el futuro (programada), no contar tiempo aún
          if (startTime > now) {
            // Calcular tiempo hasta que comience
            const timeUntilStart = (startTime - now) / 1000 / 60; // minutos hasta que comience
            const hours = Math.floor(timeUntilStart / 60);
            const minutes = Math.floor(timeUntilStart % 60);
            const seconds = Math.floor((timeUntilStart % 1) * 60);
            newTimeRemaining[report._id] = {
              total: timeUntilStart,
              hours,
              minutes,
              seconds,
              isOverdue: false,
              isScheduled: true // Indicar que está programado
            };
            return; // No procesar más para este reporte
          }

          const elapsed = (now - startTime) / 1000 / 60; // minutos transcurridos
          const remaining = report.tiempoLimite - elapsed; // minutos restantes

          if (remaining > 0) {
            const hours = Math.floor(remaining / 60);
            const minutes = Math.floor(remaining % 60);
            const seconds = Math.floor((remaining % 1) * 60);
            newTimeRemaining[report._id] = {
              total: remaining,
              hours,
              minutes,
              seconds,
              isOverdue: false
            };
          } else {
            // Tiempo vencido
            const overdue = Math.abs(remaining);
            const hours = Math.floor(overdue / 60);
            const minutes = Math.floor(overdue % 60);
            const seconds = Math.floor((overdue % 1) * 60);
            newTimeRemaining[report._id] = {
              total: -overdue,
              hours,
              minutes,
              seconds,
              isOverdue: true
            };
          }
        }
      });
      setTimeRemaining(newTimeRemaining);
    }, 1000); // Actualizar cada segundo

    return () => clearInterval(interval);
  }, [authenticated, reports]);

  const handleLogin = async (code = accessCode) => {
    if (!code) {
      alert('Por favor ingresa tu código de acceso');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post('/api/technicians/verify-access', {
        accessCode: code
      });

      if (response.data.success) {
        setTechnician(response.data.data);
        setAuthenticated(true);
        localStorage.setItem('technicianAccessCode', code);
      } else {
        alert('Código de acceso inválido');
      }
    } catch (error) {
      console.error('Error verificando acceso:', error);
      alert('Error al verificar código: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };


  const handleUpdateStatus = async (reportId, newStatus, notes = '') => {
    try {
      const response = await axios.put(`/api/technicians/reports/${reportId}`, {
        accessCode,
        status: newStatus,
        notes: notes || undefined
      });

      if (response.data.success) {
        loadReports();
        setSelectedReport(null);
        setShowCompletionModal(false);
        setCompletionComment('');
        setReportToComplete(null);
        alert('Estado actualizado exitosamente');
      }
    } catch (error) {
      console.error('Error actualizando estado:', error);
      alert('Error al actualizar estado: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCompleteWithComment = () => {
    if (!reportToComplete) return;

    if (!completionComment.trim()) {
      alert('Por favor, ingresa un comentario sobre lo que realizaste');
      return;
    }

    handleUpdateStatus(reportToComplete._id, 'completado', completionComment);
  };

  const handleLogout = () => {
    setAuthenticated(false);
    setTechnician(null);
    setAccessCode('');
    setReports([]);
    localStorage.removeItem('technicianAccessCode');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendiente': return '#ffc107';
      case 'en_proceso': return '#17a2b8';
      case 'completado': return '#28a745';
      case 'cancelado': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleString('es-DO', {
      timeZone: 'America/Santo_Domingo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateOnly = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!authenticated) {
    return (
      <div className="technician-portal">
        <div className="login-container">
          <div className="login-card">
            <h1>🔧 Portal de Técnicos</h1>
            <p>Ingresa tu código de acceso para ver tus reportes asignados</p>
            <div className="login-form">
              <input
                type="text"
                placeholder="Código de acceso (6 dígitos)"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                maxLength="6"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleLogin();
                  }
                }}
                className="access-code-input"
              />
              <button
                onClick={() => handleLogin()}
                disabled={loading || !accessCode}
                className="btn-login"
              >
                {loading ? 'Verificando...' : 'Ingresar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="technician-portal">
      <div className="portal-header">
        <div>
          <h1>🔧 Portal de Técnicos</h1>
          <p>Bienvenido, <strong>{technician?.name}</strong></p>
          {technician?.puntos !== undefined && (
            <div className="technician-points">
              <span className={`points-badge ${(technician.puntos || 0) >= 0 ? 'positive' : 'negative'}`}>
                {technician.puntos >= 0 ? '✅' : '❌'} {technician.puntos || 0} puntos
              </span>
              <span className="points-details">
                (+{technician.puntosPositivos || 0} / -{technician.puntosNegativos || 0})
              </span>
            </div>
          )}
        </div>
        <div className="header-actions">
          <button 
            onClick={() => setShowClienteRegistro(true)} 
            className="btn-register-client"
          >
            📋 Registrar Cliente
          </button>
          <button 
            onClick={() => setShowClientes(!showClientes)} 
            className="btn-view-clients"
          >
            👥 {showClientes ? 'Ver Reportes' : 'Ver Clientes'}
          </button>
          <button onClick={handleLogout} className="btn-logout">
            Cerrar Sesión
          </button>
        </div>
      </div>

      <div className="portal-content">
        <div className="filters-section">
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="filter-select"
          >
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendiente</option>
            <option value="en_proceso">En Proceso</option>
            <option value="completado">Completado</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>

        {!showClientes && (
          <div className="reports-section">
            {loading ? (
              <div className="loading-message">Cargando reportes...</div>
            ) : reports.length === 0 ? (
              <div className="empty-message">No tienes reportes asignados</div>
            ) : (
              <div className="reports-list">
                {reports.map((report) => (
                <div
                  key={report._id}
                  className={`report-card ${selectedReport?._id === report._id ? 'selected' : ''} ${timeRemaining[report._id]?.isOverdue ? 'overdue' : ''}`}
                  onClick={() => setSelectedReport(report)}
                >
                  <div className="report-header">
                    <div className="report-ticket">{report.ticketNumber}</div>
                    <div
                      className="report-status"
                      style={{ backgroundColor: getStatusColor(report.status) }}
                    >
                      {report.status === 'pendiente' ? 'Pendiente' :
                       report.status === 'en_proceso' ? 'En Proceso' :
                       report.status === 'completado' ? 'Completado' :
                       'Cancelado'}
                    </div>
                  </div>

                  {/* Contador de tiempo */}
                  {report.tiempoInicio && report.status !== 'completado' && report.tiempoLimite && timeRemaining[report._id] && (
                    <div className={`time-counter ${timeRemaining[report._id].isOverdue ? 'overdue' : ''} ${timeRemaining[report._id].isScheduled ? 'scheduled' : ''}`}>
                      {timeRemaining[report._id].isScheduled ? (
                        <div className="time-scheduled">
                          <span className="time-label">📅 Programado para comenzar en:</span>
                          <span className="time-value scheduled">
                            {String(timeRemaining[report._id].hours).padStart(2, '0')}:
                            {String(timeRemaining[report._id].minutes).padStart(2, '0')}:
                            {String(timeRemaining[report._id].seconds).padStart(2, '0')}
                          </span>
                        </div>
                      ) : timeRemaining[report._id].isOverdue ? (
                        <div className="time-overdue">
                          <span className="time-label">⚠️ Tiempo vencido:</span>
                          <span className="time-value overdue">
                            {String(timeRemaining[report._id].hours).padStart(2, '0')}:
                            {String(timeRemaining[report._id].minutes).padStart(2, '0')}:
                            {String(timeRemaining[report._id].seconds).padStart(2, '0')}
                          </span>
                        </div>
                      ) : (
                        <div className="time-remaining">
                          <span className="time-label">⏱️ Tiempo restante:</span>
                          <span className="time-value">
                            {String(timeRemaining[report._id].hours).padStart(2, '0')}:
                            {String(timeRemaining[report._id].minutes).padStart(2, '0')}:
                            {String(timeRemaining[report._id].seconds).padStart(2, '0')}
                          </span>
                        </div>
                      )}
                      <div className="time-limit-info">
                        {timeRemaining[report._id].isScheduled
                          ? `Inicia: ${formatDate(report.tiempoInicio)}`
                          : `Límite: ${report.tiempoLimite} minutos`}
                      </div>
                    </div>
                  )}

                  {report.status === 'completado' && report.completadoATiempo !== undefined && (
                    <div className={`completion-status ${report.completadoATiempo ? 'on-time' : 'late'}`}>
                      {report.completadoATiempo ? (
                        <span>✅ Completado a tiempo (+1 punto)</span>
                      ) : (
                        <span>⚠️ Completado fuera de tiempo (-1 punto)</span>
                      )}
                    </div>
                  )}
                  
                  <div className="report-body">
                    <div className="report-title">{report.title}</div>
                    <div className="report-client">Cliente: {report.clientName}</div>
                    <div className="report-phone">Tel: {report.phoneNumber}</div>
                    {report.scheduledDate && (
                      <div className="report-date">
                        📅 Programado: {formatDate(report.scheduledDate)}
                      </div>
                    )}
                    <div className="report-description">
                      {report.description.substring(0, 100)}
                      {report.description.length > 100 ? '...' : ''}
                    </div>
                  </div>
                  {selectedReport?._id === report._id && (
                    <div className="report-actions">
                      {report.status !== 'completado' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpdateStatus(report._id, 'en_proceso');
                          }}
                          className="btn-action"
                          style={{ backgroundColor: '#17a2b8' }}
                        >
                          Marcar en Proceso
                        </button>
                      )}
                      {report.status !== 'completado' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setReportToComplete(report);
                            setShowCompletionModal(true);
                            setCompletionComment('');
                          }}
                          className="btn-action"
                          style={{ backgroundColor: '#28a745' }}
                        >
                          Marcar Completado
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal para completar con comentario */}
      {showCompletionModal && (
        <div className="completion-modal-overlay" onClick={() => {
          setShowCompletionModal(false);
          setCompletionComment('');
          setReportToComplete(null);
        }}>
          <div className="completion-modal" onClick={(e) => e.stopPropagation()}>
            <div className="completion-modal-header">
              <h3>Completar Reporte</h3>
              <button
                className="completion-modal-close"
                onClick={() => {
                  setShowCompletionModal(false);
                  setCompletionComment('');
                  setReportToComplete(null);
                }}
              >
                ✕
              </button>
            </div>
            <div className="completion-modal-body">
              <p>Por favor, describe lo que realizaste para completar este reporte:</p>
              <textarea
                className="completion-comment-input"
                value={completionComment}
                onChange={(e) => setCompletionComment(e.target.value)}
                placeholder="Ejemplo: Reemplacé el router, configuré la red WiFi y verifiqué la conexión. Todo funcionando correctamente."
                rows={6}
                required
              />
            </div>
            <div className="completion-modal-footer">
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowCompletionModal(false);
                  setCompletionComment('');
                  setReportToComplete(null);
                }}
              >
                Cancelar
              </button>
              <button
                className="btn-confirm"
                onClick={handleCompleteWithComment}
                style={{ backgroundColor: '#28a745' }}
              >
                Completar Reporte
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Vista de Clientes */}
      {showClientes && (
        <div className="clientes-section">
          <h2 className="clientes-title">👥 Clientes Registrados</h2>
          {clientes.length === 0 ? (
            <div className="empty-message">No hay clientes registrados</div>
          ) : (
            <div className="clientes-grid">
              {clientes.map((cliente) => (
                <div key={cliente._id} className="cliente-card">
                  <div className="cliente-header">
                    <h3>{cliente.nombreCompleto}</h3>
                    <span className="cliente-cedula">{cliente.cedula}</span>
                  </div>
                  {cliente.imagenCedula && (
                    <div className="cliente-imagen">
                      <img src={cliente.imagenCedula} alt="Cédula" />
                    </div>
                  )}
                  <div className="cliente-info">
                    <p><strong>📍 Dirección:</strong> {cliente.direccionPrincipal}</p>
                    <p><strong>📞 WhatsApp:</strong> {cliente.telefonoWhatsApp}</p>
                    <p><strong>🔧 Caja NAT:</strong> {cliente.numeroCajaNAT}</p>
                    <p><strong>📡 WiFi:</strong> {cliente.nombreSSIDWifi}</p>
                    {cliente.coordenadasGPS?.latitud && (
                      <p>
                        <strong>🗺️ GPS:</strong> 
                        <a 
                          href={`https://www.google.com/maps?q=${cliente.coordenadasGPS.latitud},${cliente.coordenadasGPS.longitud}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="gps-link"
                        >
                          Ver en mapa
                        </a>
                      </p>
                    )}
                    <p className="cliente-fecha">
                      <strong>📅 Registrado:</strong> {new Date(cliente.fechaRegistro).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal de confirmación de completado */}
      {showCompletionModal && (
      <div className="completion-modal-overlay">
        <div className="completion-modal">
          <h3>Completar Reporte</h3>
          <p>Ticket: <strong>{reportToComplete?.ticketNumber}</strong></p>
          <p>Cliente: <strong>{reportToComplete?.clientName}</strong></p>
          
          <textarea
            className="completion-comment"
            value={completionComment}
            onChange={(e) => setCompletionComment(e.target.value)}
            placeholder="Escribe un comentario sobre el trabajo realizado..."
            rows="5"
          />

          <div className="completion-modal-footer">
            <button
              className="btn-cancel"
              onClick={() => {
                setShowCompletionModal(false);
                setCompletionComment('');
                setReportToComplete(null);
              }}
            >
              Cancelar
            </button>
            <button
              className="btn-confirm"
              onClick={handleCompleteWithComment}
              style={{ backgroundColor: '#28a745' }}
            >
              Completar Reporte
            </button>
          </div>
        </div>
      </div>
    )}

      {/* Modal para registrar cliente */}
      {showClienteRegistro && (
        <ClienteRegistro 
          accessCode={accessCode}
          onClose={() => {
            setShowClienteRegistro(false);
            loadClientes(); // Recargar clientes después de registrar
          }}
        />
      )}
      </div>
    </div>
  );
};

export default TechnicianPortal;
